import { safeParseFloat, safeParseInt } from "encamp-shared/src/tierii/utilities";
import { gql } from "generated-graphql";
import {
  ConsolidatedSiteFacilityPrefillQuery,
  ContactReportRole,
  EManifestWasteUnit,
  FacilityRcraProfileInput,
  RcraEpisodicProjectType,
  SubmissionRcraConsolidationInput,
  SubmissionRcraEpisodicEventInput,
  SubmissionRcraHsmNotificationInput,
  SubmissionRcraSiteClosureInput,
} from "generated-graphql/graphql";

import { toDatePickerValue } from "./constants";
import {
  ConsolidatedSiteValue,
  EpisodicWasteLineForm,
  FilingNotificationsFormData,
  GetSubmissionFilingNotificationsResult,
  HsmActivityForm,
  RcraProfileData,
} from "./types";

export function emptyWasteLine(): EpisodicWasteLineForm {
  return {
    wasteDescription: null,
    estimatedQuantity: null,
    quantityUnit: EManifestWasteUnit.P,
    federalWasteCodes: [],
    stateWasteCodes: [],
  };
}

export function emptyHsmActivity(): HsmActivityForm {
  return {
    facilityCode: null,
    wasteCodes: [],
    estimatedShortTons: null,
    landBasedUnit: null,
  };
}

export function buildDefaultValues(
  data: GetSubmissionFilingNotificationsResult | undefined,
  profile: RcraProfileData | null
): FilingNotificationsFormData {
  const episodic = data?.submission?.rcraEpisodicEvent ?? null;
  const closure = data?.submission?.rcraSiteClosure ?? null;
  const hsm = data?.submission?.rcraHsmNotification ?? null;
  // §13 emergency contact is now a materialized SubmissionContact; read its
  // Person back into the free-text form fields.
  const emergencyPerson = episodic?.emergencyContact?.person ?? null;
  const consolidatedSites = data?.submission?.consolidatedSites ?? [];

  return {
    // A missing row means the section was answered "No" (or never touched) —
    // both are equivalent for the filing, so default the gate to `false` rather
    // than `null`. Otherwise saving "No" (which clears the row) would re-render
    // with both radios blank instead of "No" selected.
    episodicGate: episodic ? true : false,
    isPlannedEpisodicEvent: episodic?.isPlannedEpisodicEvent ?? null,
    projectTypes: episodic?.projectTypes ?? [],
    beginningDate: toDatePickerValue(episodic?.beginningDate),
    endDate: toDatePickerValue(episodic?.endDate),
    otherDescription: episodic?.otherDescription ?? null,
    emergencyContactFirst: emergencyPerson?.first ?? null,
    emergencyContactLast: emergencyPerson?.last ?? null,
    emergencyContactEmail: emergencyPerson?.email ?? null,
    emergencyContactPhone: emergencyPerson?.phones?.[0]?.number ?? null,
    // Only a roster-linked (locked) contact re-sends its Person id; a manual
    // contact must not, or re-save would try to relink a Person the backend's
    // cleanup trigger just deleted.
    emergencyContactPersonId: episodic?.emergencyContact?.isLocked
      ? (emergencyPerson?.id ?? null)
      : null,
    wasteLines: [...(episodic?.wasteLines ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((line) => ({
        wasteDescription: line.wasteDescription ?? null,
        estimatedQuantity: line.estimatedQuantity == null ? null : String(line.estimatedQuantity),
        quantityUnit: line.quantityUnit,
        federalWasteCodes: line.federalWasteCodes ?? [],
        stateWasteCodes: line.stateWasteCodes ?? [],
      })),

    closureGate: closure ? true : false,
    closureType: closure?.closureType ?? null,
    expectedClosureDate: toDatePickerValue(closure?.expectedClosureDate),
    requestingNewClosureDateGate: closure?.requestingNewClosureDate ? true : null,
    requestingNewClosureDate: toDatePickerValue(closure?.requestingNewClosureDate),
    actualClosureDate: toDatePickerValue(closure?.actualClosureDate),

    // The gate is "are you submitting an HSM notification this filing?" (a row
    // exists), kept separate from the in-section `isManagingHsm` answer so a
    // stop-managing notification (isManagingHsm = false) still persists.
    hsmGate: hsm ? true : false,
    isManagingHsm: hsm?.isManagingHsm ?? null,
    reason: hsm?.reason ?? null,
    effectiveDate: toDatePickerValue(hsm?.effectiveDate),
    activities: [...(hsm?.activities ?? [])]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((activity) => ({
        facilityCode: activity.facilityCode ?? null,
        wasteCodes: activity.wasteCodes ?? [],
        estimatedShortTons:
          activity.estimatedShortTons == null ? null : String(activity.estimatedShortTons),
        landBasedUnit: activity.landBasedUnit ?? null,
      })),

    // The `isLqgConsolidator` gate still persists on the facility RCRA profile;
    // the sites are submission-scoped, and each site's contact fields come from
    // its materialized SubmissionContact.person.
    isLqgConsolidator: profile?.isLqgConsolidator ?? null,
    consolidatedSites: [...consolidatedSites]
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((site) => {
        const contactPerson = site.contact?.person ?? null;
        return {
          epaIdNumber: site.epaIdNumber ?? null,
          siteName: site.siteName ?? null,
          streetAddress1: site.streetAddress1 ?? null,
          streetAddress2: site.streetAddress2 ?? null,
          city: site.city ?? null,
          state: site.state ?? null,
          zipCode: site.zipCode ?? null,
          country: site.country ?? null,
          contactFirst: contactPerson?.first ?? null,
          contactLast: contactPerson?.last ?? null,
          contactPhone: contactPerson?.phones?.[0]?.number ?? null,
          contactEmail: contactPerson?.email ?? null,
          // Only a roster-linked (locked) contact re-sends its Person id (same
          // reload rule as §13's emergency contact).
          contactPersonId: site.contact?.isLocked ? (contactPerson?.id ?? null) : null,
          sortOrder: site.sortOrder ?? null,
        };
      }),
  };
}

/**
 * Builds the narrow `upsertFacilityRcraProfile` input for §14's gate: only
 * `isLqgConsolidator` is sent, so the rest of the facility's RCRA profile is
 * left untouched (the resolver's Prisma `update` ignores omitted fields).
 */
export function buildConsolidationGateInput(
  formData: FilingNotificationsFormData
): FacilityRcraProfileInput {
  return {
    isLqgConsolidator: formData.isLqgConsolidator ?? null,
  };
}

/**
 * Builds the submission-scoped consolidation input (§14). Carries the
 * `isLqgConsolidator` gate (so the validator can enforce the LQG-only rule) and
 * the sites; the resolver persists only the sites. When the site is not a
 * consolidator the sites are cleared to `[]`.
 */
export function buildConsolidationInput(
  formData: FilingNotificationsFormData
): SubmissionRcraConsolidationInput {
  return {
    isLqgConsolidator: formData.isLqgConsolidator ?? null,
    consolidatedSites:
      formData.isLqgConsolidator === true
        ? formData.consolidatedSites.map((site, index) => ({ ...site, sortOrder: index }))
        : [],
  };
}

export function emptyConsolidatedSite(): ConsolidatedSiteValue {
  return {
    epaIdNumber: null,
    siteName: null,
    streetAddress1: null,
    streetAddress2: null,
    city: null,
    state: null,
    zipCode: null,
    country: null,
    contactFirst: null,
    contactLast: null,
    contactPhone: null,
    contactEmail: null,
    contactPersonId: null,
    sortOrder: null,
  };
}

/**
 * Fetches the EPA ID, location address, and primary RCRA contact for a tenant
 * facility so it can prefill a consolidated-site row. Values are copied (not
 * linked): a consolidated site stores its address as free-text and its contact
 * as a materialized submission Person, carrying no `facilityId` FK, since
 * consolidated sites are often external (non‑Encamp) facilities.
 */
export const CONSOLIDATED_SITE_FACILITY_PREFILL = gql(`
  query ConsolidatedSiteFacilityPrefill($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      name
      streetAddress1
      streetAddress2
      city
      state
      zip
      country
      epaIdStatuses {
        epaId
        isInactive
        isPrimaryForFacility
      }
      contacts(programArea: RCRA) {
        reportingRoles
        person {
          id
          first
          last
          email
          phones {
            number
          }
        }
      }
    }
  }
`);

/** Maps a fetched tenant facility into a consolidated-site row for prefill. */
export function facilityToConsolidatedSite(
  facility: NonNullable<ConsolidatedSiteFacilityPrefillQuery["facility"]>,
  sortOrder: number | null
): ConsolidatedSiteValue {
  const primaryEpaId =
    [...(facility.epaIdStatuses ?? [])]
      .filter((status) => !status.isInactive && status.epaId)
      .sort((a, b) => {
        if (a.isPrimaryForFacility === b.isPrimaryForFacility) {
          return 0;
        }
        return a.isPrimaryForFacility ? -1 : 1;
      })[0]?.epaId ?? null;
  // Prefill the contact from the facility's RCRA site contact when present
  // (a sensible default for a consolidated site that is one of your own
  // facilities); leave it empty when there is none.
  const contact =
    facility.contacts?.find((c) => c.reportingRoles.includes(ContactReportRole.RcraSiteContact))
      ?.person ?? null;

  return {
    epaIdNumber: primaryEpaId,
    siteName: facility.name ?? null,
    streetAddress1: facility.streetAddress1 ?? null,
    streetAddress2: facility.streetAddress2 ?? null,
    city: facility.city ?? null,
    state: facility.state ?? null,
    zipCode: facility.zip ?? null,
    country: facility.country ?? null,
    contactFirst: contact?.first ?? null,
    contactLast: contact?.last ?? null,
    contactPhone: contact?.phones?.[0]?.number ?? null,
    contactEmail: contact?.email ?? null,
    // Link (and lock) the prefilled site to the matched roster Person so save
    // keeps the link instead of materializing a copy.
    contactPersonId: contact?.id ?? null,
    sortOrder,
  };
}

export function buildEpisodicInput(
  formData: FilingNotificationsFormData
): SubmissionRcraEpisodicEventInput {
  // Only persist the "other" description when an "Other" project type is
  // selected, so a value left behind after deselecting it is not saved.
  const hasOtherProjectType =
    formData.projectTypes.includes(RcraEpisodicProjectType.UnplannedOther) ||
    formData.projectTypes.includes(RcraEpisodicProjectType.PlannedOther);

  return {
    isPlannedEpisodicEvent: formData.isPlannedEpisodicEvent ?? null,
    projectTypes: formData.projectTypes,
    beginningDate: formData.beginningDate ?? null,
    endDate: formData.endDate ?? null,
    otherDescription: hasOtherProjectType ? (formData.otherDescription ?? null) : null,
    // No `emergencyContactMiddle`: Person has no middle-name column, so the
    // backend never persists it (the geppetto mapper maps middle → null).
    emergencyContactFirst: formData.emergencyContactFirst ?? null,
    emergencyContactLast: formData.emergencyContactLast ?? null,
    emergencyContactEmail: formData.emergencyContactEmail ?? null,
    emergencyContactPhone: formData.emergencyContactPhone ?? null,
    // When set, the backend links to this roster Person instead of materializing
    // a new one from the free-text fields.
    emergencyContactPersonId: formData.emergencyContactPersonId ?? null,
    wasteLines: formData.wasteLines.map((line, index) => ({
      wasteDescription: line.wasteDescription ?? null,
      estimatedQuantity:
        line.estimatedQuantity == null || line.estimatedQuantity === ""
          ? null
          : safeParseFloat(line.estimatedQuantity),
      quantityUnit: line.quantityUnit,
      federalWasteCodes: line.federalWasteCodes,
      stateWasteCodes: line.stateWasteCodes,
      sortOrder: index,
    })),
  };
}

export function buildClosureInput(
  formData: FilingNotificationsFormData
): SubmissionRcraSiteClosureInput {
  return {
    closureType: formData.closureType ?? null,
    expectedClosureDate: formData.expectedClosureDate ?? null,
    // The "requesting a new closure date" toggle gates the date itself; clear it
    // when the toggle is anything but Yes.
    requestingNewClosureDate:
      formData.requestingNewClosureDateGate === true
        ? (formData.requestingNewClosureDate ?? null)
        : null,
    actualClosureDate: formData.actualClosureDate ?? null,
  };
}

export function buildHsmInput(
  formData: FilingNotificationsFormData
): SubmissionRcraHsmNotificationInput {
  return {
    isManagingHsm: formData.isManagingHsm ?? null,
    reason: formData.reason ?? null,
    effectiveDate: formData.effectiveDate ?? null,
    activities: formData.activities.map((activity, index) => ({
      facilityCode: activity.facilityCode ?? null,
      wasteCodes: activity.wasteCodes,
      estimatedShortTons:
        activity.estimatedShortTons == null || activity.estimatedShortTons === ""
          ? null
          : safeParseInt(activity.estimatedShortTons),
      landBasedUnit: activity.landBasedUnit ?? null,
      sortOrder: index,
    })),
  };
}
